import * as THREE from 'three';
import { state } from './state.js';

/**
 * Lấy camera đang kích hoạt
 */
export function getActiveCamera() {
  return state.currentCameraType === 'orthographic' ? state.orthoCamera : state.perspCamera;
}

/**
 * Chuyển đổi Loại Phép Chiếu Camera (Perspective vs Orthographic)
 */
export function setCameraType(type) {
  state.currentCameraType = type;

  document.querySelectorAll('.cam-type-btn').forEach((btn) => {
    btn.classList.toggle('active', btn.getAttribute('data-camtype') === type);
  });

  const activeCam = getActiveCamera();
  if (state.controls) {
    state.controls.object = activeCam;
  }

  // Cập nhật vị trí đồng bộ
  if (type === 'orthographic') {
    state.orthoCamera.position.copy(state.perspCamera.position);
    state.orthoCamera.zoom = state.manualCamZoom;
    state.orthoCamera.updateProjectionMatrix();
  } else {
    state.perspCamera.position.copy(state.orthoCamera.position);
    state.perspCamera.fov = 42 / Math.sqrt(state.manualCamZoom);
    state.perspCamera.updateProjectionMatrix();
  }

  updateCameraBadges();
}

/**
 * Chuyển đổi Chế Độ Di Chuyển Camera (Parallax, Orbit, Side 90°)
 */
export function setCameraMode(mode) {
  state.currentCameraMode = mode;

  document.querySelectorAll('.cam-btn').forEach((btn) => {
    btn.classList.toggle('active', btn.getAttribute('data-mode') === mode);
  });

  const activeCam = getActiveCamera();

  if (state.controls) {
    if (mode === 'parallax') {
      state.controls.enabled = false;
    } else if (mode === 'orbit') {
      state.controls.enabled = true;
      state.controls.object = activeCam;
      state.controls.target.set(0, 3.8 + state.currentFloorHeight * 0.5, 0);
    } else if (mode === 'side') {
      state.controls.enabled = false;
    }
  }

  updateCameraBadges();
}

/**
 * Cập nhật thông tin camera lên HUD và badge
 */
export function updateCameraBadges() {
  const badge = document.getElementById('camera-badge');
  const hudLens = document.getElementById('hud-cam-lens');
  const hudMode = document.getElementById('hud-cam-mode');

  const typeName = state.currentCameraType === 'orthographic' ? 'ORTHO' : 'PERSP';
  const modeName =
    state.currentCameraMode === 'parallax' ? 'PARALLAX' : state.currentCameraMode === 'orbit' ? '3D ORBIT' : 'SIDE 90°';

  if (badge) badge.textContent = `${modeName} • ${typeName}`;
  if (hudLens) {
    hudLens.textContent =
      state.currentCameraType === 'orthographic' ? 'Orthographic (Song Song)' : 'Perspective (Phối Cảnh)';
  }
  if (hudMode) {
    hudMode.textContent =
      state.currentCameraMode === 'parallax'
        ? 'Cinematic Parallax'
        : state.currentCameraMode === 'orbit'
          ? 'Inspect 3D Orbit (360°)'
          : 'Side Profile (Cắt Ngang 90°)';
  }
}

/**
 * Điều chỉnh độ phóng đại Camera (Zoom)
 */
export function setCameraZoom(zoomVal) {
  let val = typeof zoomVal === 'number' ? zoomVal : parseFloat(zoomVal);
  if (isNaN(val)) val = 1.0;
  state.manualCamZoom = THREE.MathUtils.clamp(val, 0.4, 2.8);

  if (state.currentCameraType === 'orthographic' && state.orthoCamera) {
    state.orthoCamera.zoom = state.manualCamZoom;
    state.orthoCamera.updateProjectionMatrix();
  } else if (state.currentCameraType === 'perspective' && state.perspCamera) {
    state.perspCamera.fov = 42 / Math.sqrt(state.manualCamZoom);
    state.perspCamera.updateProjectionMatrix();
  }

  const sliderEl = document.getElementById('slider-cam-zoom');
  const tagEl = document.getElementById('val-cam-zoom');
  if (sliderEl) sliderEl.value = state.manualCamZoom;
  if (tagEl) tagEl.textContent = `${state.manualCamZoom.toFixed(2)}x`;
}

/**
 * Điều chỉnh cao độ máy quay (Height Y)
 */
export function setCameraHeight(h) {
  let val = typeof h === 'number' ? h : parseFloat(h);
  if (isNaN(val)) val = 2.8;
  state.manualCamHeight = THREE.MathUtils.clamp(val, 0.8, 6.0);

  const sliderEl = document.getElementById('slider-cam-height');
  const tagEl = document.getElementById('val-cam-height');
  if (sliderEl) sliderEl.value = state.manualCamHeight;
  if (tagEl) tagEl.textContent = `${state.manualCamHeight.toFixed(1)}m`;
}

/**
 * Điều chỉnh dịch ngang máy quay (Pan X)
 */
export function setCameraPanX(x) {
  let val = typeof x === 'number' ? x : parseFloat(x);
  if (isNaN(val)) val = 0.0;
  state.manualCamPanX = THREE.MathUtils.clamp(val, -10.0, 10.0);

  const sliderEl = document.getElementById('slider-cam-pan-x');
  const tagEl = document.getElementById('val-cam-pan-x');
  if (sliderEl) sliderEl.value = state.manualCamPanX;
  if (tagEl) {
    const sign = state.manualCamPanX > 0 ? '+' : '';
    tagEl.textContent = `${sign}${state.manualCamPanX.toFixed(1)}m`;
  }
}

/**
 * Áp dụng Preset góc máy nhanh
 */
export function applyCameraPreset(preset) {
  if (preset === 'closeup') {
    setCameraZoom(1.6);
    setCameraHeight(2.2);
    setCameraPanX(0.0);
  } else if (preset === 'medium') {
    setCameraZoom(1.0);
    setCameraHeight(2.8);
    setCameraPanX(0.0);
  } else if (preset === 'wide') {
    setCameraZoom(0.65);
    setCameraHeight(3.6);
    setCameraPanX(0.0);
  }
}

/**
 * Điều chỉnh cao độ mặt sàn 3D (Floor Height Y)
 */
export function setFloorHeight(heightY) {
  let val = typeof heightY === 'number' ? heightY : parseFloat(heightY);
  if (isNaN(val)) val = 0.0;
  state.currentFloorHeight = THREE.MathUtils.clamp(val, -3.0, 4.0);

  // Cập nhật đồng bộ các đối tượng bám sàn
  if (state.groundGroup) state.groundGroup.position.y = state.currentFloorHeight;
  if (state.contactShadowGroup) state.contactShadowGroup.position.y = state.currentFloorHeight;
  if (state.propsGroup) state.propsGroup.position.y = state.currentFloorHeight;
  if (state.seamCoverPole) state.seamCoverPole.position.y = state.currentFloorHeight;

  if (state.currentAvatar && state.currentAvatar.root) {
    state.currentAvatar.root.position.y = state.currentFloorHeight;
  }

  const sliderEl = document.getElementById('slider-floor-height');
  const tagEl = document.getElementById('val-floor-height');
  if (sliderEl) sliderEl.value = state.currentFloorHeight;
  if (tagEl) {
    const sign = state.currentFloorHeight >= 0 ? '+' : '';
    tagEl.textContent = `${sign}${state.currentFloorHeight.toFixed(2)}m`;
  }
}
